/**
 * PolicyDetailPage — `/admin/policies/:policyId`
 *
 * 4-tab detail view with lifecycle controls and a link to the simulator:
 *   1. Overview     — scope + onDeny + audit + metadata
 *   2. DSL Editor   — raw JSON with zod validation on save
 *   3. Evaluations  — recent audit rows
 *   4. Overrides    — tenant-specific overrides
 */
import {
  Archive,
  ArrowLeft,
  CaretRight,
  CheckCircle,
  EyeSlash,
  Flask,
  Trash,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Button } from '../../shared/ui/Button';
import { StatusBadge, type StatusVariant } from '../../shared/ui/StatusBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../shared/ui/Tabs';
import { cn } from '../../shared/utils/cn';
import { useBusinessPolicy } from '../../features/business-policies/useBusinessPolicies';
import type {
  PolicyLifecycleAction,
  PolicyStatus,
} from '../../features/business-policies/types';
import { PolicyLifecycleDialog } from './PolicyLifecycleDialog';
import { PolicyDslTab } from './policy-tabs/PolicyDslTab';
import { PolicyEvaluationsTab } from './policy-tabs/PolicyEvaluationsTab';
import { PolicyOverridesTab } from './policy-tabs/PolicyOverridesTab';
import { PolicyOverviewTab } from './policy-tabs/PolicyOverviewTab';

type TabId = 'overview' | 'dsl' | 'evaluations' | 'overrides';
const VALID_TABS = new Set<TabId>(['overview', 'dsl', 'evaluations', 'overrides']);

const STATUS_VARIANT: Record<PolicyStatus, StatusVariant> = {
  draft: 'neutral',
  shadow: 'info',
  active: 'success',
  deprecated: 'warning',
  archived: 'neutral',
};

const ALLOWED_ACTIONS: Record<PolicyStatus, PolicyLifecycleAction[]> = {
  draft: ['shadow', 'activate'],
  shadow: ['activate', 'deprecate'],
  active: ['deprecate'],
  deprecated: ['archive'],
  archived: [],
};

const ACTION_ICON: Record<PolicyLifecycleAction, JSX.Element> = {
  shadow: <EyeSlash size={18} weight="regular" />,
  activate: <CheckCircle size={18} weight="regular" />,
  deprecate: <Trash size={18} weight="regular" />,
  archive: <Archive size={18} weight="regular" />,
};

export function PolicyDetailPage(): JSX.Element {
  const { t } = useTranslation('adminPolicies');
  const params = useParams<{ policyId: string }>();
  const decodedId = params.policyId ? decodeURIComponent(params.policyId) : undefined;
  const policyQuery = useBusinessPolicy(decodedId);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const [lifecycleAction, setLifecycleAction] =
    useState<PolicyLifecycleAction>('shadow');

  if (!decodedId) {
    return <Navigate to="/admin/policies" replace />;
  }

  if (policyQuery.isLoading) return <LoadingSkeleton />;
  if (policyQuery.isError || !policyQuery.data) {
    return (
      <div className="px-biz-card-lg py-biz-section max-w-[1200px] mx-auto">
        <div
          role="alert"
          className="px-biz-content-lg py-biz-content-lg rounded-biz-card bg-biz-danger-subtle text-biz-danger border border-biz-danger/30"
        >
          {t('detail.notFound')}
        </div>
      </div>
    );
  }

  const record = policyQuery.data;
  const available = ALLOWED_ACTIONS[record.status];

  const openLifecycle = (action: PolicyLifecycleAction) => {
    setLifecycleAction(action);
    setLifecycleOpen(true);
  };

  const handleTab = (next: string) => {
    if (VALID_TABS.has(next as TabId)) setActiveTab(next as TabId);
  };

  return (
    <div className="px-biz-card-lg py-biz-section max-w-[1200px] mx-auto">
      <nav className="mb-biz-content-lg text-biz-sm" aria-label={t('detail.breadcrumbLabel')}>
        <ol className="flex items-center gap-biz-content text-biz-muted-foreground">
          <li>
            <Link
              to="/admin/policies"
              className="inline-flex items-center gap-1 hover:text-biz-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring rounded"
            >
              <ArrowLeft size={16} weight="regular" aria-hidden="true" />
              {t('detail.backToList')}
            </Link>
          </li>
          <li aria-hidden="true">
            <CaretRight size={14} weight="regular" />
          </li>
          <li className="text-biz-foreground font-medium truncate">{record.policyId}</li>
        </ol>
      </nav>

      <header className="flex items-start justify-between gap-biz-content-lg flex-col md:flex-row mb-biz-section">
        <div className="min-w-0">
          <div className="flex items-center gap-biz-content-lg flex-wrap mb-biz-content">
            <span className="font-mono text-biz-sm text-biz-muted-foreground">
              {record.policyId}
            </span>
            <h1 className="text-biz-3xl biz-heading font-semibold">{record.name}</h1>
            <StatusBadge
              variant={STATUS_VARIANT[record.status]}
              label={t(`status.${record.status}` as const)}
            />
          </div>
          <div className="flex items-center gap-biz-content-lg flex-wrap text-biz-sm text-biz-muted-foreground">
            <span className="font-mono">v{record.version}</span>
            <span aria-hidden="true">·</span>
            <span>
              {t('detail.priority')}{' '}
              <span className="biz-numeric font-semibold">{record.priority}</span>
            </span>
            <span aria-hidden="true">·</span>
            <span>{record.scopeActions.length} {t('detail.actionsCount')}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-biz-content-lg">
          {available.map((a) => (
            <Button
              key={a}
              intent={a === 'deprecate' || a === 'archive' ? 'danger' : 'secondary'}
              leftIcon={ACTION_ICON[a]}
              onClick={() => openLifecycle(a)}
            >
              {t(`detail.ctaLifecycle.${a}` as const)}
            </Button>
          ))}
          <Button
            intent="primary"
            leftIcon={<Flask size={18} weight="regular" />}
            asChild
          >
            <Link to={`/admin/policies/${encodeURIComponent(record.policyId)}/simulate`}>
              {t('detail.ctaSimulate')}
            </Link>
          </Button>
        </div>
      </header>

      <Tabs value={activeTab} onValueChange={handleTab} className="space-y-biz-section">
        <TabsList
          orientation="horizontal"
          aria-label={t('detail.tabsLabel')}
          className={cn('overflow-x-auto -mx-biz-card-lg px-biz-card-lg flex-nowrap')}
        >
          <TabsTrigger value="overview">{t('detail.tabs.overview')}</TabsTrigger>
          <TabsTrigger value="dsl">{t('detail.tabs.dsl')}</TabsTrigger>
          <TabsTrigger value="evaluations">{t('detail.tabs.evaluations')}</TabsTrigger>
          <TabsTrigger value="overrides">{t('detail.tabs.overrides')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <PolicyOverviewTab record={record} />
        </TabsContent>
        <TabsContent value="dsl">
          <PolicyDslTab record={record} />
        </TabsContent>
        <TabsContent value="evaluations">
          <PolicyEvaluationsTab policyId={record.policyId} />
        </TabsContent>
        <TabsContent value="overrides">
          <PolicyOverridesTab policyId={record.policyId} />
        </TabsContent>
      </Tabs>

      <PolicyLifecycleDialog
        open={lifecycleOpen}
        onOpenChange={setLifecycleOpen}
        record={record}
        action={lifecycleAction}
      />
    </div>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <div
      className="px-biz-card-lg py-biz-section max-w-[1200px] mx-auto"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="h-8 w-1/3 bg-biz-muted rounded animate-pulse mb-biz-content" />
      <div className="h-4 w-1/5 bg-biz-muted rounded animate-pulse mb-biz-section" />
      <div className="h-40 bg-biz-muted rounded-biz-card animate-pulse" />
    </div>
  );
}
